import { createSelector } from 'reselect';

import { MINIMUM_RESIDENCE_HISTORY } from '../constants/thresholds';
import { differenceInMonths } from '../lib/dateRanges';
import { nestedProperty } from '../lib/nestedProperty';
/* eslint-disable import/no-cycle */
import { somethingWentWrong } from '../pages/IneligiblePage/IneligiblePage';

export const loadingSelector = state => state.loading;
export const builderSelector = state => state.builder;
export const buildServerSelector = state => state.builder.server;
export const residenceHistorySelector = state => state.builder.ui.residenceHistory;
export const chosenCourseSelector = state => state.builder.ui.course?.value;
export const programmeStartDateSelector = state => state.builder.ui.programmeLengthStartDate;
export const programmeEndDateSelector = state => state.builder.ui.programmeLengthEndDate;

export const periodsSelector = state => state.builder.ui.periods;
export const chosenCoursePeriodsSelector = createSelector(
  periodsSelector,
  chosenCourseSelector,
  (periods, course) => (course ? periods[course] || [] : []),
);

export const chosenCourseLivePeriodsSelector = createSelector(
  chosenCoursePeriodsSelector,
  periods => {
    return periods.filter(period => period.displayStatus === true);
  },
);

export const futurePeriodsSelector = state => state.builder.ui.futurePeriods;

export const currenciesSelector = state => state.currencies;
export const validCurrencyCodesSelector = createSelector(currenciesSelector, currencies =>
  currencies.filter(c => c.value !== '').map(c => c.value),
);

export const validLoanCurrenciesSelector = createSelector(currenciesSelector, currencies =>
  currencies.filter(c => ['USD', 'GBP', 'EUR', ''].indexOf(c.value) !== -1),
);

export const validLoanCurrencyCodesSelector = createSelector(
  validLoanCurrenciesSelector,
  currencies => currencies.filter(c => c.value !== '').map(c => c.value),
);

export const campusesSelector = state => state.campuses.items;
export const fetchCampusesLoadingSelector = state => state.campuses.loading;
export const fetchCampusesErrorSelector = state => state.campuses.error;
export const campusSelector = state => state.builder.ui.campus;

export const coursesSelector = state => state.courses.items;
export const fetchCoursesLoadingSelector = state => state.courses.loading;
export const fetchCoursesErrorSelector = state => state.courses.error;
export const selectedCourseSelector = state => state.builder.ui.course;
export const periodsErrorSelector = state => state.builder.ui.periodsError;

export const isUSRelevantSelector = createSelector(
  residenceHistorySelector,
  campusSelector,
  (residenceHistory, campus) => {
    const { country = '' } = campus;
    const usResidence = residenceHistory.find(residence => residence.countryCode === 'USA');

    return country === 'USA' || usResidence !== undefined;
  },
);

export const wizardNextStepSelector = state => state.builder.wizard.currentPath;
export const wizardPathsSelector = state => state.builder.wizard.paths;

export const periodSelector = state => state.builder.ui.period;
export const periodsLoadingSelector = state => state.builder.ui.periodsLoading;

export const periodsPerClassIdSelector = createSelector(
  periodSelector,
  chosenCoursePeriodsSelector,
  (selectedPeriod, periods) => {
    if (!selectedPeriod) {
      return [];
    }

    return periods.filter(period => period.classId === selectedPeriod.classId);
  },
);

export const userSelector = state => state.user;
export const accessTokenSelector = createSelector(userSelector, user => {
  // eslint-disable-next-line camelcase
  const { access_token: accessToken = '' } = user;
  return accessToken;
});

export const customerGuidSelector = state => state.builder.ui.customerGuid;
export const courseTypeSelector = state => state.builder.ui.course.attendance;
export const isPartTimeCourseSelector = createSelector(
  courseTypeSelector,
  courseType => courseType === 'PartTime',
);
export const isFullTimeCourseSelector = createSelector(
  courseTypeSelector,
  courseType => courseType === 'FullTime',
);
export const partTimeCourseFullTimeJobSelector = state => {
  return state.builder.ui.fullTimeJob && state.builder.ui.course.attendance === 'PartTime';
};
export const fullTimeCourseEmployedSelector = state => {
  return state.builder.ui.currentlyEmployed && state.builder.ui.course.attendance === 'FullTime';
};
export const courseValidationSelector = state => state.builder.ui.course?.validated;
export const currentlyEmployedSelector = createSelector(
  partTimeCourseFullTimeJobSelector,
  fullTimeCourseEmployedSelector,
  (partTimeEmployed, fullTimeJob) => {
    if (partTimeEmployed) {
      return partTimeEmployed;
    }
    return fullTimeJob;
  },
);

export const fundsRequiredSelector = createSelector(
  campusSelector,
  periodSelector,
  (campus, period) => {
    const { country = '' } = campus;
    const { deposit } = period;

    return country !== 'USA' && deposit > 0;
  },
);

export const periodCurrencySelector = createSelector(periodSelector, period => period?.currency);

export const unpaidTuitionCostSelector = state => state.builder.ui.unpaidTuitionCost;

export const capOnLoanSelector = state => state.builder.ui.capOnLoan;

export const applicationIdSelector = state => state.builder.server.id;

export const otherFundsSelector = state => state.builder.ui.otherFunds;

export const profileSelector = state => state.user.profile;
export const emailSelector = createSelector(profileSelector, profile => {
  const { email = '' } = profile || {};
  return email;
});

export const currentPathSelector = state => state.router.location.pathname;
export const currentPathObjectSelector = state => state.builder.wizard.currentPathObject;

export const userSignedOutSelector = state => state.user.signedOut;

export const hasConfirmedJobOfferSelector = state => state.builder.ui.hasConfirmedJobOffer || false;
export const initialCurrentlyEmployedSelector = state => state.builder.ui.currentlyEmployed || null;
export const currentSalarySelector = state => state.builder.ui.currentSalary;

export const eligibilitySelector = state => nestedProperty(state, 'builder', 'ui', 'eligibility');

export const eligibilityTimeStampFromServerSelector = state =>
  nestedProperty(state, 'builder', 'server', 'eligibilityTimestamp');

export const eligibilityIdSelector = createSelector(
  eligibilitySelector,
  eligibility => eligibility.eligibilityId,
);

export const eligibilityReasonSelector = createSelector(eligibilitySelector, eligibility => {
  let reason = somethingWentWrong;
  if (eligibility) {
    reason = eligibility.formattedReason;
  }
  return reason;
});

export const residenceCountrySelector = state => state.builder.ui.residenceCountry;
export const residenceStateSelector = state => state.builder.ui.residenceState;
export const defaultCurrencySelector = createSelector(
  residenceCountrySelector,
  residenceCountry => {
    const { currency } = residenceCountry || {};

    return currency || '';
  },
);

export const isResidenceStateTexasSelector = createSelector(
  residenceCountrySelector,
  residenceStateSelector,
  (residenceCountry, residenceState) => {
    return residenceCountry.value === 'USA' && residenceState.value === 'US-TX';
  },
);

export const depositAmountSelector = state => state.builder.ui.depositAmount;
export const hasPaidDepositSelector = state => state.builder.ui.hasPaidDeposit;
export const zendeskLoadedSelector = state => state.zendesk;
export const employersSelector = state => state.employers;
export const alpha3CountriesSelector = state => state.countries.items.alpha3;
export const fetchCountriesLoadingSelector = state => state.countries.loading;
export const fetchCountriesErrorSelector = state => state.countries.error;

export const universitiesSelector = state => state.universities.items;
export const fetchUniversitiesLoadingSelector = state => state.universities.loading;
export const fetchUniversitiesErrorSelector = state => state.universities.error;
export const selectedUniversitySelector = state => state.builder.ui.previousEducationUniversity;

export const provisionalOfferSelector = state => state.provisionalOffer.item;
export const fetchProvisionalOfferLoadingSelector = state => state.provisionalOffer.loading;
export const fetchProvisionalOfferErrorSelector = state => state.provisionalOffer.error;
export const updateProvisionalOfferSelector = state => state.updateProvisionalOffer.item;
export const updateProvisionalOfferLoadingSelector = state => state.updateProvisionalOffer.loading;
export const updateProvisionalOfferErrorSelector = state => state.updateProvisionalOffer.error;

export const unvalidatedPeriodsSelector = state => state.unvalidatedPeriods.items;
export const fetchUnvalidatedPeriodsLoadingSelector = state => state.unvalidatedPeriods.loading;
export const fetchUnvalidatedPeriodsErrorSelector = state => state.unvalidatedPeriods.error;

export const subdivisionsSelector = state => state.states.items;
export const fetchStatesLoadingSelector = state => state.states.loading;
export const fetchStatesErrorSelector = state => state.states.error;

export const schoolsSelector = state => state.schools.items;
export const fetchSchoolsLoadingSelector = state => state.schools.loading;
export const fetchSchoolsErrorSelector = state => state.schools.error;

export const selectedSchoolSelector = state => state.builder.ui.school;
export const signedOutErrorSelector = state => state.signedOutError;

export const uiCitizenshipCountriesSelector = state => state.builder.ui.citizenshipCountries;
export const serverCitizenshipCountriesSelector = state =>
  state.builder.server.citizenshipCountries;
export const selectedCitizenshipCountryCodesSelector = createSelector(
  uiCitizenshipCountriesSelector,
  serverCitizenshipCountriesSelector,
  (uiCitizenshipCountries, serverCitizenshipCountries) => {
    if (uiCitizenshipCountries.length) {
      return uiCitizenshipCountries.map(citizenship => citizenship.value);
    }

    return serverCitizenshipCountries || [];
  },
);

export const urlFromErrorSelector = error => nestedProperty(error, 'error', 'config', 'url');
export const actionFromErrorSelector = error => nestedProperty(error, 'error', 'action', 'type');
export const hasPreviousProdigyLoanSelector = state => state.builder.ui.hasPreviousProdigyLoan;
export const minLoanAmountSelector = state => state.builder.ui.minLoanAmount;

export const sameResidenceForThreeYearsSelector = state =>
  state.builder.ui.sameResidenceForThreeYears;
export const residenceThresholdMetSelector = state => {
  const now = new Date(Date.now());
  let dateExpectationsMet = false;
  if (state.builder.ui.residenceHistory && state.builder.ui.residenceHistory.length) {
    const { startMonth, startYear } = state.builder.ui.residenceHistory[0];
    const diffInMonths = differenceInMonths(new Date(startYear, startMonth, 1), now);
    dateExpectationsMet = diffInMonths >= MINIMUM_RESIDENCE_HISTORY;
  }
  return dateExpectationsMet;
};
export const periodsTimeStampSelector = state => state.builder.ui.periodsTimeStamp;
